import { c as _c } from "react/compiler-runtime";
import { useViewPort } from "@website/hooks/viewport";
import { MaterialIcon } from "@website/icons/material";
import { Button } from "@website/ui/button";
import { MiniPopover } from "@website/ui/mini-popover";
import { Skeleton } from "@website/ui/skeleton";
import { StripFilterPanel, getStripFilterButtonClassName, getStripFilterContainerClassName, useStripFilterAnimation } from "@website/widget/filter/filter-components/strip";
import { getStripFilterOptionStaggerKey } from "@website/widget/filter/filter-components/strip.utils";
import { useHorizontalDragScroll } from "@website/widget/filter/hotel-filter-chips/horizontal-drag-scroll.hook";
import { HotelFilterChipsPropertiesTypes } from "@website/widget/filter/hotel-filter-chips/hotel-filter-chips.types";
import { useHotelFiltersChips } from "@website/widget/filter/hotel-filter-chips/hotel-filters-chips.hook";
import { useItemStagger } from "@website/widget/item-stagger/item-stagger.hook";
import { useRouter } from "next/router";
import { Fragment, MouseEvent, useEffect, useState } from "react";
const FILTER_CHIP_SKELETON_ITEM_KEYS = Array.from({
  length: 6
}, (_, index) => `filter-chip-skeleton-${index}`);
const STRIP_SKELETON_ITEM_KEYS = Array.from({
  length: 6
}, (_, index) => `strip-skeleton-${index}`);
const STRIP_ITEM_STAGGER_START_INDEX = STRIP_SKELETON_ITEM_KEYS.length;
const getStripItemStaggerIndexes = (chipsFiltersSchema: HotelFilterChipsPropertiesTypes["chipsFiltersSchema"]) => {
  const indexes: Record<string, number> = {};
  let index = STRIP_ITEM_STAGGER_START_INDEX;
  for (const [filterName, schema] of Object.entries(chipsFiltersSchema)) {
    if (schema.type !== "strip") continue;
    for (const option of schema.options || []) {
      indexes[getStripFilterOptionStaggerKey(filterName, option.name)] = index;
      index += 1;
    }
  }
  return indexes;
};
const Component = properties => {
  const $ = _c(60);
  const {
    chipsFiltersSchema,
    classname,
    hasFilter,
    hidePopoverActions,
    loading,
    isNewPlP,
    stripTriggerInView: t0,
    shouldAnimateStripItems: t1
  } = properties;
  const stripTriggerInView = t0 === undefined ? true : t0;
  const shouldAnimateStripItems = t1 === undefined ? true : t1;
  const {
    isMobile
  } = useViewPort();
  const horizontalDragScrollProperties = useHorizontalDragScroll();
  let t2;
  if ($[0] !== chipsFiltersSchema) {
    t2 = getStripItemStaggerIndexes(chipsFiltersSchema);
    $[0] = chipsFiltersSchema;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const stripItemStaggerIndexes = t2;
  let t3;
  if ($[2] !== loading || $[3] !== shouldAnimateStripItems || $[4] !== stripItemStaggerIndexes) {
    t3 = shouldAnimateStripItems ? loading ? STRIP_SKELETON_ITEM_KEYS : Object.keys(stripItemStaggerIndexes) : [];
    $[2] = loading;
    $[3] = shouldAnimateStripItems;
    $[4] = stripItemStaggerIndexes;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const stripStaggerItemKeys = t3;
  const t4 = loading ? "loading" : "loaded";
  let t5;
  if ($[6] !== t4) {
    t5 = {
      continueFromVisibleItems: true,
      refreshKey: t4
    };
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    getItemStaggerProperties
  } = useItemStagger(stripStaggerItemKeys, t5);
  const t6 = shouldAnimateStripItems ? getItemStaggerProperties : undefined;
  let t7;
  if ($[8] !== properties || $[9] !== stripItemStaggerIndexes || $[10] !== t6) {
    t7 = {
      ...properties,
      getStripItemStaggerProperties: t6,
      stripItemStaggerIndexes
    };
    $[8] = properties;
    $[9] = stripItemStaggerIndexes;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const {
    chips,
    children,
    defaultStripKey,
    onClick,
    onClose,
    onReset,
    onSubmit,
    onTextClick,
    buttonProperties
  } = useHotelFiltersChips(t7);
  const {
    closeVisibleStrip,
    entryDirection,
    handleTransitionEnd,
    isExiting: stripIsExiting,
    openStrip,
    renderedStripKey
  } = useStripFilterAnimation(defaultStripKey, stripTriggerInView);
  const [completedInitialStripKey, setCompletedInitialStripKey] = useState();
  const router = useRouter();
  let t8;
  if ($[12] !== router?.query) {
    t8 = router?.query.popover === "price_range" ? !Object?.keys(router?.query)?.includes(String("price_from")) && !Object?.keys(router?.query)?.includes(String("price_to")) : !Object?.keys(router?.query)?.includes(String(router?.query.popover));
    $[12] = router?.query;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const isResetDisabled = t8;
  const shouldReserveInitialStripHeight = isNewPlP && Boolean(defaultStripKey) && stripTriggerInView && completedInitialStripKey !== defaultStripKey && !stripIsExiting;
  let t10;
  let t9;
  if ($[14] !== completedInitialStripKey || $[15] !== defaultStripKey || $[16] !== loading || $[17] !== renderedStripKey || $[18] !== stripIsExiting) {
    t9 = () => {
      if (loading || !defaultStripKey) {
        setCompletedInitialStripKey(undefined);
        return;
      }
      if (renderedStripKey !== defaultStripKey || stripIsExiting) {
        return;
      }
      if (completedInitialStripKey === defaultStripKey) {
        return;
      }
      const timeoutId = window.setTimeout(() => setCompletedInitialStripKey(defaultStripKey), 420);
      return () => window.clearTimeout(timeoutId);
    };
    t10 = [completedInitialStripKey, defaultStripKey, loading, renderedStripKey, stripIsExiting];
    $[14] = completedInitialStripKey;
    $[15] = defaultStripKey;
    $[16] = loading;
    $[17] = renderedStripKey;
    $[18] = stripIsExiting;
    $[19] = t10;
    $[20] = t9;
  } else {
    t10 = $[19];
    t9 = $[20];
  }
  useEffect(t9, t10);
  let t11;
  if ($[21] !== closeVisibleStrip || $[22] !== onClick || $[23] !== onClose || $[24] !== onTextClick || $[25] !== openStrip) {
    t11 = (key, isStripFilter, isTextFilter, isStripMounted, isPopoverOpen, event) => {
      if (isTextFilter) {
        onTextClick(key, event);
        return;
      }
      if (isStripFilter) {
        if (isStripMounted) {
          closeVisibleStrip();
          return;
        }
        openStrip(key);
        return;
      }
      if (isPopoverOpen) {
        onClose([], event);
        return;
      }
      onClick([key], event);
    };
    $[21] = closeVisibleStrip;
    $[22] = onClick;
    $[23] = onClose;
    $[24] = onTextClick;
    $[25] = openStrip;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const handleFilterClick = t11;
  let t12;
  if ($[27] !== buttonProperties) {
    t12 = event_0 => {
      event_0.stopPropagation();
      buttonProperties.onClick?.();
    };
    $[27] = buttonProperties;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const handleAllFiltersClick = t12;
  let t13;
  if ($[29] !== buttonProperties || $[30] !== handleAllFiltersClick || $[31] !== hasFilter) {
    t13 = hasFilter ? <Button className="!shrink-0 !whitespace-nowrap" {...buttonProperties} onClick={handleAllFiltersClick} /> : null;
    $[29] = buttonProperties;
    $[30] = handleAllFiltersClick;
    $[31] = hasFilter;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const allFiltersButton = t13;
  const t14 = `${classname || ""} relative flex min-w-0 flex-col`;
  const t15 = isNewPlP && (loading || shouldReserveInitialStripHeight) ? 102 : undefined;
  let t16;
  if ($[33] !== t15) {
    t16 = {
      minHeight: t15
    };
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== allFiltersButton || $[36] !== children || $[37] !== chips || $[38] !== defaultStripKey || $[39] !== entryDirection || $[40] !== getItemStaggerProperties || $[41] !== handleFilterClick || $[42] !== handleTransitionEnd || $[43] !== hidePopoverActions || $[44] !== horizontalDragScrollProperties || $[45] !== isMobile || $[46] !== isNewPlP || $[47] !== isResetDisabled || $[48] !== loading || $[49] !== onClose || $[50] !== onReset || $[51] !== onSubmit || $[52] !== renderedStripKey || $[53] !== shouldAnimateStripItems || $[54] !== stripIsExiting) {
    t17 = loading && isNewPlP ? <div className="flex h-[102px] min-w-0 flex-col flex-nowrap gap-[14px] overflow-hidden"><div className="scrollbar-none flex min-w-0 flex-row gap-2 overflow-x-auto">{FILTER_CHIP_SKELETON_ITEM_KEYS.map(_temp)}</div><div className="flex min-w-0 flex-row gap-6 overflow-x-clip pt-1">{STRIP_SKELETON_ITEM_KEYS.map((itemKey_0, index) => <div key={itemKey_0} {...shouldAnimateStripItems ? getItemStaggerProperties(itemKey_0, index, {
          className: "shrink-0"
        }) : {
          className: "shrink-0"
        }}><Skeleton shape="rectangle" height={48} width={64} /></div>)}</div></div> : <><div className="scrollbar-none flex min-w-0 cursor-grab select-none flex-row flex-nowrap gap-2 overflow-x-auto active:cursor-grabbing lg:cursor-auto lg:select-auto xl:overflow-visible" {...horizontalDragScrollProperties}>{!defaultStripKey && allFiltersButton}{chips?.map(chip => {
          const isStripVisible = chip.isStripFilter && renderedStripKey === chip.key && !stripIsExiting;
          return <Fragment key={chip.key}><div className={getStripFilterContainerClassName(chip.isStripFilter)}><Button StartIcon={chip.icon} EndIcon={chip.isStripFilter ? <MaterialIcon className="text-on-surface-medium-emphasis" name={isStripVisible ? "arrow_drop_up" : "arrow_drop_down"} /> : chip.hasValue ? <MaterialIcon className="text-body-1 relative z-[9] cursor-pointer" name="cancel" onClick={event_1 => onClose(chip.resetKeys, event_1)} variant="fill" /> : undefined} className={getStripFilterButtonClassName(chip.isStripFilter)} color={chip.hasValue ? "secondary" : "neutral"} iconColorMode={chip.hasValue ? "inherit" : "custom"} onClick={event_2 => handleFilterClick(chip.key, chip.isStripFilter, chip.isTextFilter, isStripVisible, chip.isOpen, event_2)} size="small" text={chip.title} variant="round-outline" />{!chip.isStripFilter && !chip.isTextFilter && <MiniPopover defaultHorizontalPosition="start" className={`shadow-2 overflow-hidden rounded-xl ${chip.shouldExpandPopover ? "lg:!w-[440px]" : ""}`} onClose={() => onClose([])} open={chip.isOpen} title={chip.popoverTitle} usePortal={isMobile}><div className="flex !w-full flex-col px-4 pb-4">{chip.selectedChild}{!hidePopoverActions && <div className="flex w-full flex-row items-center justify-between md:hidden"><Button className="w-[140px]" color="primary" onClick={onReset} text={"\u067E\u0627\u06A9 \u06A9\u0631\u062F\u0646"} variant="text" disabled={isResetDisabled} /><Button className="w-[140px]" color="secondary" onClick={onSubmit} text={"\u062A\u0627\u06CC\u06CC\u062F \u0627\u0646\u062A\u062E\u0627\u0628\u200C\u0647\u0627"} variant="contained" /></div>}</div></MiniPopover>}</div>{chip.key === defaultStripKey && allFiltersButton}</Fragment>;
        })}</div><StripFilterPanel entryDirection={entryDirection} isExiting={stripIsExiting} onTransitionEnd={handleTransitionEnd} renderedStripKey={renderedStripKey}>{children}</StripFilterPanel></>;
    $[35] = allFiltersButton;
    $[36] = children;
    $[37] = chips;
    $[38] = defaultStripKey;
    $[39] = entryDirection;
    $[40] = getItemStaggerProperties;
    $[41] = handleFilterClick;
    $[42] = handleTransitionEnd;
    $[43] = hidePopoverActions;
    $[44] = horizontalDragScrollProperties;
    $[45] = isMobile;
    $[46] = isNewPlP;
    $[47] = isResetDisabled;
    $[48] = loading;
    $[49] = onClose;
    $[50] = onReset;
    $[51] = onSubmit;
    $[52] = renderedStripKey;
    $[53] = shouldAnimateStripItems;
    $[54] = stripIsExiting;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== t14 || $[57] !== t16 || $[58] !== t17) {
    t18 = <div className={t14} style={t16}>{t17}</div>;
    $[56] = t14;
    $[57] = t16;
    $[58] = t17;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  return t18;
};
export const HotelFilterChips = Component;
function _temp(itemKey) {
  return <Skeleton shape="rectangle" className="shrink-0 rounded-full" height={36} key={itemKey} width={120} />;
}